import { Theme, css } from '@emotion/react';
import React from 'react';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { fontStyles } from '~src/theme/fontStyles';

import { FoldButton } from '../../category/fold-button';
import { useGlobalState, useSessionState } from '../../global-state';
import { SplitDeliveryCartItem } from './split-delivery-cart-item';

export const SplitDeliveryCartManager: React.FC = () => {
  const { tICU } = useTranslations();
  const globalState = useGlobalState();
  const session = useSessionState();
  const [folded, setFolded] = React.useState(true);

  if (
    !session.cart.items ||
    // @ts-ignore TODO: prepare for single cart force display cart
    (globalState.sessions.length === 1 && !session.flags.display_cart)
  ) {
    return null;
  }

  const visibleItems = folded ? session.cart.items.slice(0, 2) : session.cart.items;

  const elements = visibleItems.map((item, idx) => (
    <SplitDeliveryCartItem key={idx} cartItem={item} isFolded={folded} />
  ));

  const renderDescription = () => {
    if (!folded) return null;

    const itemsInCart = session.cart.items?.length;
    const firstVisibleElement = visibleItems[0];
    if (itemsInCart === undefined) return null;

    if (itemsInCart === 1) {
      return (
        <div css={styles.itemName}>
          <span>{firstVisibleElement.name}</span>
          {firstVisibleElement.quantity !== undefined && (
            <span css={styles.itemQuantity}>(×{firstVisibleElement.quantity})</span>
          )}
        </div>
      );
    }

    if (itemsInCart === 2) {
      return (
        <div css={styles.itemName}>
          <span>
            {tICU('widget.cart.SPLIT_NUMBER_PRODUCT_PLACEHOLDER', {
              first_product_name: firstVisibleElement.name ?? '',
            })}
          </span>
        </div>
      );
    }

    return (
      <div css={styles.itemName}>
        <span>
          {tICU('widget.cart.SPLIT_NUMBER_PRODUCTS_PLACEHOLDER', {
            first_product_name: firstVisibleElement.name ?? '',
            number_of_products_left: itemsInCart - 1,
          })}
        </span>
      </div>
    );
  };

  return (
    <>
      <div css={styles.splitDeliveryContainer(folded)}>
        <div css={styles.cartItemsWrapper}>
          <div css={styles.listWrapper(folded)}>{elements}</div>
        </div>
        {session.cart.items.length >= 2 && (
          <div css={styles.foldButton} data-branding="split-delivery-fold-button">
            <FoldButton folded={folded} onClick={() => setFolded(oldFolded => !oldFolded)} />
          </div>
        )}
      </div>
      {renderDescription()}
    </>
  );
};

const styles = {
  splitDeliveryContainer: (isFolded: boolean) =>
    css({
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      paddingBottom: !isFolded ? '16px' : '0',
    }),
  cartItemsWrapper: css({
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  }),
  wrapper: (theme: Theme) => ({
    borderTop: `1px solid ${theme.neutral90}`,
    padding: '20px 0',
  }),
  listWrapper: (isFolded: boolean) =>
    css({
      display: 'flex',
      flexDirection: isFolded ? 'row' : 'column',
      ...(!isFolded && { gap: '2rem' }),
    }),

  itemName: css({
    paddingTop: '12px',
    ...fontStyles.px14_20,
  }),
  singleCartItem: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    ...fontStyles.px14_20,
  }),
  itemQuantity: {
    paddingLeft: '5px',
  },
  foldButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};
